import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Image,
  Input,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiX, FiRefreshCw } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AppBar, APP } from "../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../components/desktopUi";
import { appImages } from "../assets/appicons";
import { uploadImageApi, uploadImageByLinkApi } from "../api/attendance.api";
import { extractErrorMessage } from "../api/client";
import { resolveImageUrl } from "../utils/format";
import {
  createEmployeeRequestApi,
  getBranchListApi,
  getSaleAreaListApi,
  getSaleBlockListApi,
  getJobTitleListApi,
  getJobPositionListApi,
  getBusinessCategoryListApi,
  getSupportDepartmentListApi,
} from "../api/ext.registerEmployee.api";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../config";

const BORDER = "#DCE1EA";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const GRAD = "linear(to-r, #68A2FF, #2168DA)";
const GENDERS = [{ id: 0, name: "Nam" }, { id: 1, name: "Nữ" }, { id: 2, name: "Khác" }];

const Label = ({ children, required }) => (
  <Text fontSize="13px" fontWeight={600} mb="6px" color={TEXT}>
    {children}{required && <Text as="span" color="#EF2E14"> *</Text>}
  </Text>
);

const ErrText = ({ error }) => (error ? <Text fontSize="12px" color="red.500" mt="4px">{error}</Text> : null);

const TextField = ({ label, required, value, onChange, placeholder, type = "text", error }) => (
  <Box mb="16px">
    <Label required={required}>{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type} h="46px" fontSize="14px" borderColor={error ? "#EF2E14" : BORDER} borderRadius="8px" />
    <ErrText error={error} />
  </Box>
);

const SelectField = ({ label, required, value, onOpen, error }) => (
  <Box mb="16px">
    <Label required={required}>{label}</Label>
    <Flex as="button" type="button" onClick={onOpen} align="center" justify="space-between" h="46px" px="12px" border="1px solid" borderColor={error ? "#EF2E14" : BORDER} borderRadius="8px" w="100%">
      <Text fontSize="14px" color={value ? TEXT : MUTED} noOfLines={1}>{value || "Chọn..."}</Text>
      <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
    </Flex>
    <ErrText error={error} />
  </Box>
);

// Chụp ảnh thật bằng camera (getUserMedia) — thay cho input capture không mở được camera trên desktop
const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [err, setErr] = useState("");

  useEffect(() => {
    let active = true;
    const stop = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("no camera");
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch {
        if (active) setErr("Không truy cập được camera. Hãy cấp quyền camera cho trình duyệt, hoặc dùng \"Tải ảnh\".");
      }
    })();
    return () => { active = false; stop(); };
  }, [facing]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onCapture(new File([blob], `camera_${Date.now()}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92
    );
  };

  return (
    <Box position="fixed" inset="0" bg="black" zIndex={2000} display="flex" flexDirection="column">
      <Flex align="center" justify="space-between" px="16px" h="52px" color="white" flexShrink={0}>
        <Box as="button" type="button" onClick={onClose} aria-label="Đóng"><FiX size={24} /></Box>
        <Text fontSize="15px" fontWeight={600}>Chụp ảnh</Text>
        <Box as="button" type="button" onClick={() => setFacing((v) => (v === "environment" ? "user" : "environment"))} aria-label="Đổi camera"><FiRefreshCw size={20} /></Box>
      </Flex>
      {err ? (
        <Flex flex="1" align="center" justify="center" px="24px"><Text color="white" textAlign="center" fontSize="14px">{err}</Text></Flex>
      ) : (
        <Box flex="1" overflow="hidden">
          <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </Box>
      )}
      <Flex justify="center" align="center" py="20px" bg="black" flexShrink={0}>
        {err ? (
          <Button variant="outline" color="white" borderColor="white" onClick={onClose} _hover={{ bg: "whiteAlpha.200" }}>Đóng</Button>
        ) : (
          <Box as="button" type="button" onClick={capture} w="66px" h="66px" borderRadius="full" bg="white" border="4px solid" borderColor="#B7C3D6" aria-label="Chụp" />
        )}
      </Flex>
    </Box>
  );
};

// Nút thay ảnh nhỏ (góc dưới phải preview)
const ReplaceBtn = ({ icon, text, onClick }) => (
  <Flex as="button" type="button" onClick={onClick} align="center" gap="4px" bg="white" px="6px" py="2px" borderRadius="10px" boxShadow="0 0 4px rgba(0,0,0,0.15)">
    <img src={icon} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
    <Text fontSize="12px" fontWeight={600} color={APP.primary200}>{text}</Text>
  </Flex>
);

// Upload ảnh khớp app v2 (FormImageUpload): tab thiết bị / đường dẫn, tải + chụp, phải bấm tải lên để đồng bộ
const ImageUpload = ({ label, required, path, onChange, error }) => {
  const [mode, setMode] = useState("upload"); // upload | link
  const [pending, setPending] = useState(null); // { file, preview }
  const [link, setLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const libRef = useRef(null);

  const extractPath = (d) => (typeof d === "string" ? d : d?.filePath || d?.uri || d?.path || d?.url || "");

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPending({ file, preview: URL.createObjectURL(file) });
  };
  const onCamera = (file) => {
    setPending({ file, preview: URL.createObjectURL(file) });
    setCameraOpen(false);
  };
  const doUpload = async () => {
    if (!pending?.file) return;
    setUploading(true);
    try {
      const d = await uploadImageApi(pending.file);
      onChange(extractPath(d));
      setPending(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Tải ảnh thất bại"));
    } finally {
      setUploading(false);
    }
  };
  const syncLink = async () => {
    const url = link.trim();
    if (!/^https?:\/\/\S+$/i.test(url)) { toast.error("Link ảnh không hợp lệ"); return; }
    setUploading(true);
    try {
      const d = await uploadImageByLinkApi(url);
      onChange(extractPath(d) || url);
      setLink("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Tải ảnh từ link thất bại"));
    } finally {
      setUploading(false);
    }
  };

  const H = "220px";
  const TabBtn = ({ active, children, onClick }) => (
    <Box as="button" type="button" onClick={onClick} px="16px" py="6px" bg={active ? APP.primary200 : "white"}>
      <Text fontSize="12px" fontWeight={600} color={active ? "white" : MUTED} whiteSpace="nowrap">{children}</Text>
    </Box>
  );

  return (
    <Box mb="16px">
      <Label required={required}>{label}</Label>

      {/* Switcher: thiết bị / đường dẫn */}
      <Flex display="inline-flex" border="1px solid" borderColor={BORDER} borderRadius="8px" overflow="hidden">
        <TabBtn active={mode === "upload"} onClick={() => setMode("upload")}>Tải ảnh từ thiết bị</TabBtn>
        <TabBtn active={mode === "link"} onClick={() => setMode("link")}>Sử dụng đường dẫn có sẵn</TabBtn>
      </Flex>
      <Text fontSize="12px" color="#0BA5EC" pt="5px">
        {mode === "link"
          ? "( Sau khi lựa chọn xong ảnh, bắt buộc phải ấn đồng bộ ảnh về hệ thống để hệ thống đồng bộ )"
          : "( Sau khi lựa chọn xong ảnh, bắt buộc phải ấn tải ảnh lên để hệ thống đồng bộ )"}
      </Text>

      {mode === "upload" && pending && (
        <Box mt="8px">
          <Box position="relative" border="1px solid" borderColor={BORDER} borderRadius="8px" p="1px" overflow="hidden">
            <Image src={pending.preview} alt="" w="100%" h={H} objectFit="contain" borderRadius="8px" />
            <Flex position="absolute" bottom="8px" right="8px" direction="column" align="flex-end" gap="8px">
              <ReplaceBtn icon={appImages.icon_upload_image} text="Tải ảnh lại" onClick={() => libRef.current?.click()} />
              <ReplaceBtn icon={appImages.icon_upload_photo} text="Chụp ảnh lại" onClick={() => setCameraOpen(true)} />
            </Flex>
          </Box>
          <Text fontSize="12px" color="red.500" textAlign="right" pt="12px">Bắt buộc phải tải ảnh lên hệ thống</Text>
          <Flex justify="flex-end" mt="8px">
            <Button w="120px" h="36px" bgImage={GRAD} color="white" borderRadius="8px" fontWeight={700} fontSize="14px" _hover={{ bgImage: GRAD, opacity: 0.92 }} isLoading={uploading} onClick={doUpload}>Tải ảnh lên</Button>
          </Flex>
        </Box>
      )}

      {mode === "upload" && !pending && path && (
        <Box mt="8px" position="relative">
          <Box border="1px solid" borderColor={BORDER} borderRadius="8px" p="1px" overflow="hidden">
            <Image src={resolveImageUrl(path, EMP_API_BASE_URL)} alt="" w="100%" h={H} objectFit="contain" borderRadius="8px" />
          </Box>
          <Flex as="button" type="button" onClick={() => onChange("")} position="absolute" top="12px" right="4px" bg="white" p="4px" borderRadius="full" boxShadow="0 0 4px rgba(0,0,0,0.15)" align="center" justify="center">
            <img src={appImages.icon_bin} alt="" width={20} height={20} style={{ objectFit: "contain" }} />
          </Flex>
          <Flex position="absolute" bottom="8px" right="8px" direction="column" align="flex-end" gap="8px">
            <ReplaceBtn icon={appImages.icon_upload_image} text="Tải ảnh lại" onClick={() => libRef.current?.click()} />
            <ReplaceBtn icon={appImages.icon_upload_photo} text="Chụp ảnh lại" onClick={() => setCameraOpen(true)} />
          </Flex>
        </Box>
      )}

      {mode === "upload" && !pending && !path && (
        <Box mt="8px" h={H} border="1px solid" borderColor={BORDER} borderRadius="8px" overflow="hidden">
          <Flex h="100%" align="center" justify="space-evenly" px="12px">
            <Flex as="button" type="button" onClick={() => libRef.current?.click()} direction="column" align="center" gap="8px">
              <img src={appImages.icon_upload_image} alt="" width={40} height={40} style={{ objectFit: "contain", opacity: 0.5 }} />
              <Text fontSize="14px" color={MUTED}>Tải ảnh của bạn lên</Text>
            </Flex>
            <Flex as="button" type="button" onClick={() => setCameraOpen(true)} direction="column" align="center" gap="8px">
              <img src={appImages.icon_upload_photo} alt="" width={40} height={40} style={{ objectFit: "contain", opacity: 0.5 }} />
              <Text fontSize="14px" color={MUTED}>Chụp ảnh của bạn lên</Text>
            </Flex>
          </Flex>
        </Box>
      )}

      {mode === "link" && (
        <Box mt="8px">
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Dán link ảnh (https://...)" h="44px" fontSize="14px" borderColor={BORDER} borderRadius="8px" />
          <Flex justify="flex-end" mt="8px">
            <Button w="200px" h="36px" bgImage={GRAD} color="white" borderRadius="8px" fontWeight={700} fontSize="14px" _hover={{ bgImage: GRAD, opacity: 0.92 }} isLoading={uploading} onClick={syncLink}>Đồng bộ ảnh về hệ thống</Button>
          </Flex>
        </Box>
      )}

      <input ref={libRef} type="file" accept="image/*" hidden onChange={onPick} />
      {cameraOpen && <CameraCapture onCapture={onCamera} onClose={() => setCameraOpen(false)} />}
      {error ? <Text fontSize="12px" color="red.500" mt="4px">{error}</Text> : null}
    </Box>
  );
};

const RegisterEmployee = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [tab, setTab] = useState("BUSINESS"); // BUSINESS | SUPPORT
  const [f, setF] = useState({});
  const [errors, setErrors] = useState({});
  const set = (k) => (v) => {
    setF((s) => ({ ...s, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const [submitting, setSubmitting] = useState(false);

  // jobPosition tách theo khối bộ phận vì mỗi khối chỉ được chọn một số chức vụ
  const [opts, setOpts] = useState({ branch: [], saleArea: [], saleBlock: [], jobTitle: [], jobPositionBusiness: [], jobPositionSupport: [], businessCategory: [], supportDepartment: [] });

  const sheet = useDisclosure();
  const [sheetKey, setSheetKey] = useState(null); // gender | branch | ...

  useEffect(() => {
    Promise.all([
      getBranchListApi(), getSaleAreaListApi(), getSaleBlockListApi(),
      getJobTitleListApi(), getJobPositionListApi(1), getJobPositionListApi(0),
      getBusinessCategoryListApi(), getSupportDepartmentListApi(),
    ]).then(([branch, saleArea, saleBlock, jobTitle, jobPositionBusiness, jobPositionSupport, businessCategory, supportDepartment]) => {
      setOpts({
        branch: branch?.items || branch || [],
        saleArea: saleArea?.items || saleArea || [],
        saleBlock: saleBlock?.items || saleBlock || [],
        jobTitle: jobTitle?.items || jobTitle || [],
        jobPositionBusiness: jobPositionBusiness?.items || jobPositionBusiness || [],
        jobPositionSupport: jobPositionSupport?.items || jobPositionSupport || [],
        businessCategory: businessCategory?.items || businessCategory || [],
        supportDepartment: supportDepartment?.items || supportDepartment || [],
      });
    }).catch(() => {});
  }, []);

  const sheetConfig = {
    gender: { title: "Giới tính", options: GENDERS, valueKey: "gender", labelKey: "genderLabel" },
    branch: { title: "Chi nhánh", options: opts.branch, valueKey: "branchId", labelKey: "branchLabel" },
    saleArea: { title: "Vùng", options: opts.saleArea, valueKey: "saleAreaId", labelKey: "saleAreaLabel" },
    saleBlock: { title: "Khối", options: opts.saleBlock, valueKey: "saleBlockId", labelKey: "saleBlockLabel" },
    jobTitle: { title: "Chức danh", options: opts.jobTitle, valueKey: "jobTitleId", labelKey: "jobTitleLabel" },
    jobPosition: { title: "Chức vụ", options: tab === "BUSINESS" ? opts.jobPositionBusiness : opts.jobPositionSupport, valueKey: "jobPositionId", labelKey: "jobPositionLabel" },
    businessCategory: { title: "Hạng mục kinh doanh", options: opts.businessCategory, valueKey: "businessCategoryId", labelKey: "businessCategoryLabel" },
    supportDepartment: { title: "Phòng ban hỗ trợ", options: opts.supportDepartment, valueKey: "supportDepartmentId", labelKey: "supportDepartmentLabel" },
  };

  const openSheet = (k) => { setSheetKey(k); sheet.onOpen(); };
  const pickOption = (o) => {
    const cfg = sheetConfig[sheetKey];
    setF((s) => ({ ...s, [cfg.valueKey]: o.id ?? o._id, [cfg.labelKey]: o.name }));
    setErrors((e) => (e[cfg.valueKey] ? { ...e, [cfg.valueKey]: undefined } : e));
    sheet.onClose();
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validate = () => {
    const e = {};
    const req = (k, msg) => { if (!f[k] && f[k] !== 0) e[k] = msg; };
    req("avatar", "Ảnh đại diện là bắt buộc");
    req("fullName", "Họ tên là trường bắt buộc");
    if (f.gender === undefined || f.gender === null || f.gender === "") e.gender = "Vui lòng chọn giới tính";
    if (!f.phone?.trim()) e.phone = "Số điện thoại là trường bắt buộc";
    else if (!/^[0-9]{10,12}$/.test(f.phone.trim())) e.phone = "Số điện thoại không hợp lệ";
    if (!f.email?.trim()) e.email = "Email là trường bắt buộc";
    else if (!EMAIL_RE.test(f.email.trim())) e.email = "Email không đúng định dạng";
    req("identificationNumber", "Số CCCD/CMND là bắt buộc");
    req("idCardFrontImage", "Ảnh CCCD mặt trước là bắt buộc");
    req("idCardBackImage", "Ảnh CCCD mặt sau là bắt buộc");
    req("branchId", "Chi nhánh là bắt buộc");
    if (tab === "BUSINESS") {
      req("saleAreaId", "Vùng là bắt buộc");
      req("saleBlockId", "Khối là bắt buộc");
      req("businessCategoryId", "Loại nhân sự là bắt buộc");
    } else {
      req("supportDepartmentId", "Phòng ban là bắt buộc");
    }
    req("jobTitleId", "Chức danh là bắt buộc");
    req("jobPositionId", "Chức vụ là bắt buộc");
    req("bankAccount", "Số tài khoản là bắt buộc");
    if (!f.contractEmail?.trim()) e.contractEmail = "Email hợp đồng là bắt buộc";
    else if (!EMAIL_RE.test(f.contractEmail.trim())) e.contractEmail = "Email không đúng định dạng";
    return e;
  };

  const onSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      toast.error(Object.values(e)[0]);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        fullName: f.fullName?.trim(),
        gender: f.gender,
        phone: f.phone?.trim(),
        email: f.email?.trim(),
        identificationNumber: f.identificationNumber?.trim(),
        avatar: f.avatar || undefined,
        idCardFrontImage: f.idCardFrontImage || undefined,
        idCardBackImage: f.idCardBackImage || undefined,
        branchId: f.branchId,
        jobTitleId: f.jobTitleId,
        jobPositionId: f.jobPositionId,
        bankAccount: f.bankAccount?.trim(),
        contractEmail: f.contractEmail?.trim(),
        ...(tab === "BUSINESS"
          ? { saleAreaId: f.saleAreaId, saleBlockId: f.saleBlockId, businessCategoryId: f.businessCategoryId }
          : { supportDepartmentId: f.supportDepartmentId }),
      };
      await createEmployeeRequestApi(body);
      toast.success("Gửi giới thiệu nhân viên thành công");
      navigate(EMP_BASE_PATH);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Gửi thất bại"));
    } finally {
      setSubmitting(false);
    }
  };

  const pageBody = (
    <>

      <Box px="16px" pt="12px">
        {/* Tabs */}
        <Flex bg="#F7F9FC" borderRadius="8px" p="4px" mb="16px">
          {[{ k: "BUSINESS", label: "Kinh doanh" }, { k: "SUPPORT", label: "Hỗ trợ kinh doanh" }].map((t) => (
            <Flex key={t.k} as="button" type="button" flex="1" h="40px" align="center" justify="center" borderRadius="8px" bg={tab === t.k ? APP.primary200 : "transparent"} onClick={() => { setTab(t.k); setErrors({}); setF((s) => ({ ...s, jobPositionId: undefined, jobPositionLabel: undefined })); }}>
              <Text fontSize="14px" fontWeight={600} color={tab === t.k ? "white" : APP.gray}>{t.label}</Text>
            </Flex>
          ))}
        </Flex>

        <ImageUpload label="Ảnh đại diện" required path={f.avatar} onChange={set("avatar")} error={errors.avatar} />
        <TextField label="Họ và tên" required value={f.fullName || ""} onChange={set("fullName")} placeholder="Nhập họ tên" error={errors.fullName} />
        <SelectField label="Giới tính" required value={f.genderLabel} onOpen={() => openSheet("gender")} error={errors.gender} />
        <TextField label="Số điện thoại" required value={f.phone || ""} onChange={set("phone")} placeholder="Nhập số điện thoại" error={errors.phone} />
        <TextField label="Email" required value={f.email || ""} onChange={set("email")} placeholder="Nhập email" error={errors.email} />
        <TextField label="Số CCCD/CMND" required value={f.identificationNumber || ""} onChange={set("identificationNumber")} placeholder="Nhập số CCCD/CMND" error={errors.identificationNumber} />
        <ImageUpload label="CCCD mặt trước" required path={f.idCardFrontImage} onChange={set("idCardFrontImage")} error={errors.idCardFrontImage} />
        <ImageUpload label="CCCD mặt sau" required path={f.idCardBackImage} onChange={set("idCardBackImage")} error={errors.idCardBackImage} />
        <SelectField label="Chi nhánh" required value={f.branchLabel} onOpen={() => openSheet("branch")} error={errors.branchId} />
        {tab === "BUSINESS" && (
          <>
            <SelectField label="Vùng" required value={f.saleAreaLabel} onOpen={() => openSheet("saleArea")} error={errors.saleAreaId} />
            <SelectField label="Khối" required value={f.saleBlockLabel} onOpen={() => openSheet("saleBlock")} error={errors.saleBlockId} />
          </>
        )}
        <SelectField label="Chức danh" required value={f.jobTitleLabel} onOpen={() => openSheet("jobTitle")} error={errors.jobTitleId} />
        <SelectField label="Chức vụ" required value={f.jobPositionLabel} onOpen={() => openSheet("jobPosition")} error={errors.jobPositionId} />
        {tab === "BUSINESS" ? (
          <SelectField label="Hạng mục kinh doanh" required value={f.businessCategoryLabel} onOpen={() => openSheet("businessCategory")} error={errors.businessCategoryId} />
        ) : (
          <SelectField label="Phòng ban hỗ trợ" required value={f.supportDepartmentLabel} onOpen={() => openSheet("supportDepartment")} error={errors.supportDepartmentId} />
        )}
        <TextField label="Số tài khoản ngân hàng" required value={f.bankAccount || ""} onChange={set("bankAccount")} placeholder="Nhập số tài khoản" error={errors.bankAccount} />
        <TextField label="Email hợp đồng" required value={f.contractEmail || ""} onChange={set("contractEmail")} placeholder="Nhập email hợp đồng" error={errors.contractEmail} />

        <Button mt="8px" w="100%" h="50px" bgImage={GRAD} color="white" borderRadius="10px" fontWeight={700} _hover={{ bgImage: GRAD, opacity: 0.92 }} isLoading={submitting} onClick={onSubmit}>
          Gửi giới thiệu
        </Button>
      </Box>

      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">{sheetKey ? sheetConfig[sheetKey].title : ""}</Text>
            <Box maxH="55vh" overflowY="auto">
              {(sheetKey ? sheetConfig[sheetKey].options : []).map((o, i, arr) => {
                const id = o.id ?? o._id;
                const active = sheetKey && f[sheetConfig[sheetKey].valueKey] === id;
                return (
                  <Flex key={String(id) + i} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={i === arr.length - 1 ? "none" : "1px solid #EEF1F6"} onClick={() => pickOption(o)}>
                    <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400} textAlign="left">{o.name}</Text>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Giới thiệu việc làm" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" pb="24px">
      <AppBar title="Giới thiệu việc làm" onBack={() => navigate(EMP_BASE_PATH)} />
      {pageBody}
    </Box>
  );
};

export default RegisterEmployee;
